import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Modal,
  Select,
  Tabs,
  message,
} from '@surecompanies/core_components'
import { Table, Tag, Tooltip } from 'antd'
import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import DownloadOutlined from '@ant-design/icons/DownloadOutlined'
import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import InboxOutlined from '@ant-design/icons/InboxOutlined'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import ReloadOutlined from '@ant-design/icons/ReloadOutlined'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { US_STATE_OPTIONS } from '../pages/PlanOverrides/constants'
import {
  downloadPlanBenefitsExcel,
  getActivePlanBenefitsImportJob,
  getPlanBenefitHiosPlanIds,
  getPlanBenefitUploadLogDownloadUrl,
  getPlanBenefitUploadLogs,
  getPlanBenefitYears,
  getPlanBenefitsImportJob,
  presignPlanBenefitsImport,
  startPlanBenefitsImport,
  type ImportPlanBenefitsResponseDto,
  type PlanBenefitUploadLogDto,
  type PlanBenefitUploadLogS3Dto,
} from '../api/plan-management-tasks-api'
import { uploadToS3 } from '../utils/uploadToS3'

const { TabPane } = Tabs

const MAX_FILE_SIZE = 100 * 1024 * 1024
const MAX_ERRORS_SHOWN = 10

type ImportPhase = 'idle' | 'uploading' | 'processing' | 'result'

const JOB_POLL_INTERVAL_MS = 2_000
const MAX_POLL_FAILURES = 3
const TERMINAL_JOB_STATES = new Set([
  'completed',
  'failed',
  'cancelled',
  'expired',
])

// Presigned URLs are generated on click (not eagerly for every row) — they
// expire quickly, so there's no point minting one before the user actually
// wants the file.
function S3DownloadLink({ label, s3Key }: { label: string; s3Key: string }) {
  const [isLoading, setIsLoading] = useState(false)
  const filename = s3Key.split('/').pop() ?? s3Key

  const handleDownload = useCallback(async () => {
    setIsLoading(true)
    try {
      const url = await getPlanBenefitUploadLogDownloadUrl(s3Key)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to get download URL', err)
      message.error('Could not generate a download link. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }, [s3Key])

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
      <span
        style={{
          color: '#6b7280',
          minWidth: 120,
          flexShrink: 0,
          fontSize: 12,
        }}
      >
        {label}
      </span>
      <Tooltip title={s3Key}>
        <Button
          type="link"
          size="small"
          icon={isLoading ? <LoadingOutlined /> : <DownloadOutlined />}
          disabled={isLoading}
          onClick={() => void handleDownload()}
          style={{
            padding: 0,
            height: 'auto',
            fontSize: 12,
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          <span
            style={{
              fontFamily: 'monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {filename}
          </span>
        </Button>
      </Tooltip>
    </div>
  )
}

function escapeCsvField(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

const UPLOAD_LOGS_CSV_HEADERS = [
  'Time',
  'Uploaded By',
  'File',
  'Status',
  'Updated',
  'Total Rows',
  'Row Errors',
]

function buildUploadLogsCsv(rows: PlanBenefitUploadLogDto[]): string {
  const lines = rows.map((row) => {
    const d = row.additionalData
    return [
      dayjs(row.timestamp).format('YYYY-MM-DD HH:mm:ss'),
      row.actorName,
      d?.originalFilename ?? '',
      d?.success ? 'Applied' : 'Rejected',
      String(d?.updated ?? ''),
      String(d?.totalRows ?? ''),
      String(d?.rowErrors ?? ''),
    ]
      .map(escapeCsvField)
      .join(',')
  })
  return [UPLOAD_LOGS_CSV_HEADERS.join(','), ...lines].join('\n')
}

interface Props {
  open: boolean
  onClose: () => void
}

export default function PlanBenefitsExcelModal({ open, onClose }: Props) {
  const { flags } = useFeatureFlags()
  const isUploadEnabled =
    flags['admin.plan-management-plan-benefits-csv-upload-enabled'] === true

  const [activeTab, setActiveTab] = useState<'download' | 'upload' | 'logs'>(
    'download'
  )

  // --- Download tab ---
  const [year, setYear] = useState<number | undefined>(undefined)
  const [filterByState, setFilterByState] = useState(false)
  const [states, setStates] = useState<string[]>([])
  const [filterByHiosPlan, setFilterByHiosPlan] = useState(false)
  const [hiosPlanIds, setHiosPlanIds] = useState<string[]>([])
  const [isDownloading, setIsDownloading] = useState(false)

  const { data: availableYears, isLoading: isLoadingYears } = useQuery({
    queryKey: ['planBenefitYears'],
    queryFn: getPlanBenefitYears,
    enabled: open,
  })

  const effectiveStates = filterByState ? states : []

  const { data: availableHiosPlanIds, isLoading: isLoadingHiosPlanIds } =
    useQuery({
      queryKey: ['planBenefitHiosPlanIds', year, effectiveStates],
      queryFn: () => getPlanBenefitHiosPlanIds(year as number, effectiveStates),
      enabled: open && year !== undefined && filterByHiosPlan,
    })

  useEffect(() => {
    if (
      open &&
      availableYears &&
      availableYears.length > 0 &&
      year === undefined
    ) {
      setYear(availableYears[0])
    }
  }, [open, availableYears, year])

  // The HIOS Plan ID options depend on year + state — a previously selected
  // HIOS Plan ID may no longer be valid once either filter changes.
  useEffect(() => {
    setHiosPlanIds([])
  }, [year, filterByState, states])

  const handleDownload = useCallback(async () => {
    if (year === undefined) return
    if (filterByState && effectiveStates.length === 0) return
    if (filterByHiosPlan && hiosPlanIds.length === 0) return
    setIsDownloading(true)
    try {
      await downloadPlanBenefitsExcel({
        year,
        states: effectiveStates,
        hiosPlanIds: filterByHiosPlan ? hiosPlanIds : [],
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to download plan benefits csv', err)
      message.error('An error occurred. Please try again.')
    } finally {
      setIsDownloading(false)
    }
  }, [year, filterByState, effectiveStates, filterByHiosPlan, hiosPlanIds])

  const isDownloadDisabled =
    year === undefined ||
    (filterByState && states.length === 0) ||
    (filterByHiosPlan && hiosPlanIds.length === 0)

  // --- Upload tab ---
  const [phase, setPhase] = useState<ImportPhase>('idle')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadPercent, setUploadPercent] = useState(0)
  const [result, setResult] = useState<ImportPlanBenefitsResponseDto | null>(
    null
  )
  const [isDragOver, setIsDragOver] = useState(false)
  // Filename for a job resumed on reopen — there's no File object for it, unlike selectedFile.
  const [resumedFilename, setResumedFilename] = useState<string | null>(null)
  // True from modal-open until the active-job check resolves — keeps the
  // upload UI non-interactive so a user can't start a new upload in the
  // window before we know whether one is already running.
  const [isCheckingActiveJob, setIsCheckingActiveJob] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Guards against a poll callback that's already mid-flight (awaiting the
  // network call) when the component unmounts — clearTimeout only cancels a
  // timeout that hasn't fired yet, not one whose callback is already running.
  const isCancelledRef = useRef(false)
  // Consecutive poll-check failures (e.g. a transient network blip on
  // GET /jobs/:id) — only give up after MAX_POLL_FAILURES in a row, since the
  // job itself may still be running fine server-side.
  const pollFailureCountRef = useRef(0)

  const resetUploadState = useCallback(() => {
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
    pollFailureCountRef.current = 0
    setPhase('idle')
    setSelectedFile(null)
    setUploadPercent(0)
    setResult(null)
    setResumedFilename(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [])

  useEffect(() => {
    return () => {
      isCancelledRef.current = true
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
    }
  }, [])

  const validateAndSetFile = useCallback((file: File) => {
    const isCsv = file.type === 'text/csv' || file.name.endsWith('.csv')
    if (!isCsv) {
      message.error('Only .csv files are allowed.')
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      message.error('File exceeds the 100 MB limit.')
      return
    }
    setSelectedFile(file)
  }, [])

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) validateAndSetFile(file)
      e.target.value = ''
    },
    [validateAndSetFile]
  )

  // Recursive setTimeout, not setInterval, so a slow check can't overlap the next one.
  const pollJob = useCallback((jobId: string) => {
    pollTimeoutRef.current = setTimeout(() => {
      void (async () => {
        if (isCancelledRef.current) return
        try {
          const job = await getPlanBenefitsImportJob(jobId)
          if (isCancelledRef.current) return
          pollFailureCountRef.current = 0
          if (job.state === 'completed' && job.output) {
            setResult(job.output)
            setPhase('result')
            return
          }
          if (TERMINAL_JOB_STATES.has(job.state)) {
            message.error('Import failed. Please try again.')
            setPhase('idle')
            return
          }
          pollJob(jobId)
        } catch (err) {
          if (isCancelledRef.current) return
          // eslint-disable-next-line no-console
          console.error('Failed to check import job status', err)
          pollFailureCountRef.current += 1
          if (pollFailureCountRef.current < MAX_POLL_FAILURES) {
            pollJob(jobId)
            return
          }
          message.error('Could not check import status. Please try again.')
          setPhase('idle')
        }
      })()
    }, JOB_POLL_INTERVAL_MS)
  }, [])

  // Resume an in-progress import (started before the modal was closed/reopened) instead of showing a blank form.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setIsCheckingActiveJob(true)
    void (async () => {
      try {
        const activeJob = await getActivePlanBenefitsImportJob()
        if (cancelled) return
        if (activeJob.jobId) {
          setResumedFilename(activeJob.originalFilename)
          setPhase('processing')
          pollFailureCountRef.current = 0
          pollJob(activeJob.jobId)
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to check for an in-progress import', err)
      } finally {
        if (!cancelled) setIsCheckingActiveJob(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, pollJob])

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return
    setPhase('uploading')
    setUploadPercent(0)
    try {
      const { folder, uploadUrl } = await presignPlanBenefitsImport(
        selectedFile.name
      )
      const uploadResult = await uploadToS3({
        presignedUrl: uploadUrl,
        file: selectedFile,
        contentType: 'text/csv',
        onProgress: setUploadPercent,
      })
      if (!uploadResult.success) {
        throw uploadResult.error ?? new Error('S3 upload failed')
      }

      const { jobId } = await startPlanBenefitsImport(folder, selectedFile.name)
      setPhase('processing')
      pollFailureCountRef.current = 0
      pollJob(jobId)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to import plan benefits csv', err)
      message.error('Import failed. Please try again.')
      setPhase('idle')
    }
  }, [selectedFile, pollJob])

  const handleClose = useCallback(() => {
    if (isDownloading || phase === 'uploading' || phase === 'processing') {
      return
    }
    setActiveTab('download')
    resetUploadState()
    onClose()
  }, [isDownloading, phase, resetUploadState, onClose])

  const isResult = phase === 'result'

  // --- Upload Logs tab ---
  const [logsPage, setLogsPage] = useState(1)
  const [logsPageSize, setLogsPageSize] = useState(10)
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null)

  const {
    data: logsData,
    isLoading: isLoadingLogs,
    isFetching: isFetchingLogs,
    refetch: refetchLogs,
  } = useQuery({
    queryKey: ['planBenefitUploadLogs', logsPage, logsPageSize],
    queryFn: () =>
      getPlanBenefitUploadLogs({ page: logsPage, limit: logsPageSize }),
    enabled: open && isUploadEnabled && activeTab === 'logs',
    placeholderData: (previousData) => previousData,
  })

  const [isExportingLogs, setIsExportingLogs] = useState(false)

  const handleExportLogsCsv = useCallback(async () => {
    setIsExportingLogs(true)
    try {
      // "Export CSV" covers every matching log, not just the current page —
      // fetch a single large page rather than looping, per the "simple" ask.
      const { rows } = await getPlanBenefitUploadLogs({ page: 1, limit: 5000 })
      const csv = buildUploadLogsCsv(rows)
      // Leading BOM so Excel opens the file as UTF-8 instead of guessing.
      const blob = new Blob(['\uFEFF', csv], {
        type: 'text/csv;charset=utf-8;',
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `plan-benefit-upload-logs-${dayjs().format('YYYYMMDD-HHmmss')}.csv`
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to export upload logs csv', err)
      message.error('Export failed. Please try again.')
    } finally {
      setIsExportingLogs(false)
    }
  }, [])

  const logColumns = [
    {
      title: 'Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 160,
      render: (timestamp: string) =>
        dayjs(timestamp).format('MMM D, YYYY h:mm A'),
    },
    {
      title: 'Uploaded By',
      dataIndex: 'actorName',
      key: 'actorName',
      width: 160,
      ellipsis: true,
    },
    {
      title: 'File',
      key: 'file',
      ellipsis: true,
      render: (_: unknown, record: PlanBenefitUploadLogDto) => {
        const filename = record.additionalData?.originalFilename ?? 'Unknown'
        return (
          <Tooltip title={filename}>
            <span>
              <FileTextOutlined style={{ marginRight: 6, color: '#9ca3af' }} />
              {filename}
            </span>
          </Tooltip>
        )
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_: unknown, record: PlanBenefitUploadLogDto) =>
        record.additionalData?.success ? (
          <Tag color="green">Applied</Tag>
        ) : (
          <Tag color="red">Rejected</Tag>
        ),
    },
    {
      title: 'Rows',
      key: 'rows',
      width: 180,
      render: (_: unknown, record: PlanBenefitUploadLogDto) => {
        const d = record.additionalData
        if (!d) return '—'
        const totalRows = d.totalRows ?? '?'
        return d.success
          ? `${d.updated ?? '?'} of ${totalRows} updated`
          : `0 applied — ${d.rowErrors ?? '?'} of ${totalRows} failed`
      },
    },
  ]

  const renderLogDetails = useCallback((record: PlanBenefitUploadLogDto) => {
    const d = record.additionalData
    if (!d) return <span style={{ fontSize: 12 }}>No details available.</span>

    const years = d.years ?? []
    // Older entries only have the full array — fall back to its length.
    const hiosPlanIdsCount =
      d.hiosPlanIdsCount ?? d.hiosPlanIdsFilter?.length ?? 0
    const s3: Partial<PlanBenefitUploadLogS3Dto> = d.s3 ?? {}

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {d.errorMessage && (
          <span style={{ fontSize: 12, color: '#cf1322' }}>
            <strong>Error:</strong> {d.errorMessage}
          </span>
        )}
        <div
          style={{ display: 'flex', flexWrap: 'wrap', gap: 24, fontSize: 12 }}
        >
          <span>
            <strong>Years:</strong> {years.length > 0 ? years.join(', ') : '—'}
          </span>
          <span>
            <strong>HIOS Plan IDs Count:</strong> {hiosPlanIdsCount}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: '#374151' }}>
            S3 files
          </span>
          {s3.originalFile && (
            <S3DownloadLink label="Uploaded file" s3Key={s3.originalFile} />
          )}
          {s3.updateSql && (
            <S3DownloadLink label="Generated SQL" s3Key={s3.updateSql} />
          )}
          {s3.errors && <S3DownloadLink label="Row errors" s3Key={s3.errors} />}
          {s3.diff && (
            <S3DownloadLink label="Diff (changed rows)" s3Key={s3.diff} />
          )}
          {s3.preUpdateBackup && (
            <S3DownloadLink
              label="Pre-update backup"
              s3Key={s3.preUpdateBackup}
            />
          )}
          {!s3.originalFile &&
            !s3.updateSql &&
            !s3.errors &&
            !s3.diff &&
            !s3.preUpdateBackup && <span>—</span>}
        </div>
      </div>
    )
  }, [])

  return (
    <Modal
      open={open}
      title="Plan Benefits CSV"
      onCancel={handleClose}
      maskClosable={false}
      footer={null}
      width={activeTab === 'logs' ? 900 : 560}
      destroyOnClose
    >
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as 'download' | 'upload')}
      >
        <TabPane tab="Download" key="download">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Select
              placeholder="Year"
              loading={isLoadingYears}
              value={year}
              onChange={(v) => setYear(v)}
              options={(availableYears ?? []).map((y) => ({
                label: String(y),
                value: y,
              }))}
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: '#374151' }}>
                {filterByState ? 'Selecting Specific States' : 'All States'}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Checkbox
                  aria-label="Filter by state"
                  checked={filterByState}
                  onChange={(e) => {
                    setFilterByState(e.target.checked)
                    if (!e.target.checked) setStates([])
                  }}
                />
                <Select
                  style={{ flex: 1 }}
                  mode="multiple"
                  placeholder="Select states"
                  showSearch
                  optionFilterProp="label"
                  disabled={!filterByState}
                  value={states}
                  onChange={(v) => setStates(v)}
                  options={US_STATE_OPTIONS}
                  maxTagCount="responsive"
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: '#374151' }}>
                {filterByHiosPlan
                  ? 'Selecting Specific HIOS Plans'
                  : 'All HIOS Plans'}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Checkbox
                  aria-label="Filter by HIOS Plan"
                  checked={filterByHiosPlan}
                  onChange={(e) => {
                    setFilterByHiosPlan(e.target.checked)
                    if (!e.target.checked) setHiosPlanIds([])
                  }}
                />
                <Select
                  style={{ flex: 1 }}
                  mode="multiple"
                  placeholder="Select HIOS Plan IDs"
                  showSearch
                  optionFilterProp="label"
                  disabled={!filterByHiosPlan || year === undefined}
                  loading={isLoadingHiosPlanIds}
                  value={hiosPlanIds}
                  onChange={(v) => setHiosPlanIds(v)}
                  options={(availableHiosPlanIds ?? []).map((id) => ({
                    label: id,
                    value: id,
                  }))}
                  maxTagCount="responsive"
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={isDownloading}
                disabled={isDownloadDisabled}
                onClick={() => void handleDownload()}
              >
                Download
              </Button>
            </div>
          </div>
        </TabPane>

        {isUploadEnabled && (
          <TabPane tab="Upload" key="upload">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Alert
                type="warning"
                showIcon
                message="This only updates existing rows by PlanBenefitID — no rows are inserted or deleted."
              />

              {isResult && result && (
                <Alert
                  type={result.success ? 'success' : 'error'}
                  showIcon
                  message={
                    result.errorMessage
                      ? `Import failed unexpectedly: ${result.errorMessage}`
                      : result.success
                        ? `Updated ${result.updated} of ${result.totalRows} rows (${result.skipped} unchanged, skipped).`
                        : `Nothing was applied — ${result.rowErrors} of ${result.totalRows} row(s) failed validation.`
                  }
                  description={
                    result.errors.length > 0 ? (
                      <ul
                        style={{
                          margin: '4px 0 0',
                          paddingLeft: 20,
                          fontSize: 12,
                        }}
                      >
                        {result.errors.slice(0, MAX_ERRORS_SHOWN).map((e) => (
                          <li key={e.row}>
                            Row #{e.row}: {e.message}
                          </li>
                        ))}
                        {result.errors.length > MAX_ERRORS_SHOWN && (
                          <li>
                            ...and {result.errors.length - MAX_ERRORS_SHOWN}{' '}
                            more
                          </li>
                        )}
                      </ul>
                    ) : undefined
                  }
                />
              )}

              {!isResult && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleFileChange}
                    disabled={phase !== 'idle' || isCheckingActiveJob}
                    style={{ display: 'none' }}
                    aria-label="Select CSV file"
                  />

                  {isCheckingActiveJob ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '32px 16px',
                        justifyContent: 'center',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        color: '#6b7280',
                      }}
                    >
                      <LoadingOutlined />
                      Checking for an in-progress import…
                    </div>
                  ) : selectedFile || phase !== 'idle' ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px 16px',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                      }}
                    >
                      <FileTextOutlined
                        style={{ fontSize: 24, color: '#6b7280' }}
                      />
                      <span style={{ flex: 1, fontWeight: 500 }}>
                        {selectedFile?.name ?? resumedFilename}
                        {phase === 'uploading' && (
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 400,
                              color: '#6b7280',
                              marginTop: 4,
                            }}
                          >
                            Uploading… {uploadPercent}%
                          </div>
                        )}
                        {phase === 'processing' && (
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 400,
                              color: '#6b7280',
                              marginTop: 4,
                            }}
                          >
                            <LoadingOutlined style={{ marginRight: 6 }} />
                            Processing your upload…
                          </div>
                        )}
                      </span>
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => setSelectedFile(null)}
                        disabled={phase !== 'idle'}
                        aria-label="Remove file"
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 8,
                        padding: '32px 16px',
                        border: `2px dashed ${isDragOver ? '#1677ff' : '#d9d9d9'}`,
                        borderRadius: 8,
                        cursor: 'pointer',
                        backgroundColor: isDragOver ? '#f0f5ff' : undefined,
                      }}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => {
                        e.preventDefault()
                        setIsDragOver(true)
                      }}
                      onDragLeave={() => setIsDragOver(false)}
                      onDrop={(e) => {
                        e.preventDefault()
                        setIsDragOver(false)
                        const file = e.dataTransfer.files?.[0]
                        if (file) validateAndSetFile(file)
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label="Upload CSV file"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ')
                          fileInputRef.current?.click()
                      }}
                    >
                      <InboxOutlined
                        style={{ fontSize: 40, color: '#1677ff' }}
                      />
                      <span style={{ color: '#6b7280' }}>
                        Click or drag a CSV file here
                      </span>
                      <span style={{ fontSize: 12, color: '#bfbfbf' }}>
                        Max 100 MB &middot; .csv only
                      </span>
                    </div>
                  )}
                </>
              )}

              <div
                style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}
              >
                {isResult ? (
                  <Button type="primary" onClick={resetUploadState}>
                    Upload another
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    onClick={() => void handleUpload()}
                    loading={phase === 'uploading' || phase === 'processing'}
                    disabled={
                      !selectedFile ||
                      phase === 'uploading' ||
                      phase === 'processing' ||
                      isCheckingActiveJob
                    }
                  >
                    {phase === 'processing' ? 'Processing…' : 'Upload'}
                  </Button>
                )}
              </div>
            </div>
          </TabPane>
        )}

        {isUploadEnabled && (
          <TabPane tab="Upload Logs" key="logs">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div
                style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}
              >
                <Button
                  type="text"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => void handleExportLogsCsv()}
                  loading={isExportingLogs}
                >
                  Export CSV
                </Button>
                <Button
                  type="text"
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => void refetchLogs()}
                  loading={isFetchingLogs}
                >
                  Refresh
                </Button>
              </div>
              <Table<PlanBenefitUploadLogDto>
                rowKey="id"
                size="small"
                loading={isLoadingLogs}
                dataSource={logsData?.rows ?? []}
                columns={logColumns}
                scroll={{ x: 760 }}
                expandable={{
                  expandedRowKeys: expandedLogId != null ? [expandedLogId] : [],
                  onExpand: (expanded, record) =>
                    setExpandedLogId(expanded ? record.id : null),
                  expandedRowRender: renderLogDetails,
                }}
                pagination={{
                  current: logsPage,
                  pageSize: logsPageSize,
                  total: logsData?.total ?? 0,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                  size: 'small',
                  onChange: (page, pageSize) => {
                    setLogsPage(page)
                    setLogsPageSize(pageSize)
                  },
                }}
                locale={{ emptyText: 'No uploads yet.' }}
              />
            </div>
          </TabPane>
        )}
      </Tabs>
    </Modal>
  )
}
